import { useGLTF } from '@react-three/drei';
import { DRACOLoader } from 'three-stdlib';
import type { GLTF } from 'three-stdlib';
import type { ObjectMap } from '@react-three/fiber';
import { DEFAULT_CAR_MODEL_ID, findCarModelById } from '../../data/carModels';
import type { CarAsset } from '../../types';

/**
 * 차량 GLB를 불러오는 유일한 진입점.
 *
 * 왜 한 모듈로 모았는가:
 *   drei의 `useGLTF`와 `useGLTF.preload`는 캐시 키가 `[GLTFLoader, url]`뿐이고
 *   로더 확장(extensions)은 키에 들어가지 않습니다. 그래서 preload와 실제 로드에
 *   서로 다른 확장을 넘기면, **먼저 로드를 시작한 쪽의 확장만** 적용됩니다.
 *   preload 쪽에 Draco 디코더를 안 붙이면 그 GLB는 디코더 없이 파싱되어 깨집니다.
 *   호출부가 셋(로드 / 기본 모델 preload / 카드 hover prefetch)이라 인자를 따로
 *   적어 두면 언제든 어긋날 수 있으므로, 여기서만 넘기게 했습니다.
 */

/**
 * Draco 디코더 하나를 앱 전체가 공유합니다.
 *
 * drei도 자체 싱글턴을 갖고 있지만(core/Gltf.js) 그건 모듈 내부 변수라 밖에서 만질 수
 * 없습니다. 그래서 `useDraco: false`로 drei 쪽을 끄고 우리 인스턴스를 붙입니다.
 * 우리가 들고 있어야 아래 preload가 가능합니다.
 */
let sharedDecoder: DRACOLoader | null = null;
let sharedDecoderPath: string | null = null;

/**
 * 주의: 디코더는 첫 초기화 때 경로를 확정합니다(three-stdlib이 `decoderPending`을
 * 캐시합니다). 카탈로그의 모든 모델이 같은 `dracoDecoderPath`를 쓰는 것을 전제로
 * 하며, carModels.test.ts가 그 값의 형태를 검사합니다. 모델마다 다른 경로를 쓰려면
 * 인스턴스를 경로별로 나눠야 합니다.
 */
function getSharedDecoder(decoderPath: string): DRACOLoader {
  if (sharedDecoder === null) {
    sharedDecoder = new DRACOLoader();
  }
  if (sharedDecoderPath !== decoderPath) {
    sharedDecoder.setDecoderPath(decoderPath);
    sharedDecoderPath = decoderPath;
  }
  return sharedDecoder;
}

/**
 * 디코더 라이브러리를 미리 받아 두고 wasm까지 컴파일해 둡니다.
 *
 * 왜 필요한가:
 *   GLTFLoader는 GLB를 통째로 받은 **뒤에** 파싱을 시작하고, Draco 프리미티브를
 *   만난 그 시점에야 디코더를 요청합니다. 즉 아무것도 하지 않으면
 *   `GLB 다운로드 완료 → 디코더 248KB 왕복 → 디코드` 순서로 직렬화됩니다.
 *   먼저 불러 두면 그 왕복이 GLB 다운로드와 겹쳐서 사라집니다.
 *
 *   워커는 여기서 만들어지지 않습니다. three-stdlib은 실제 디코드 요청이 올 때
 *   워커를 만듭니다. 그래서 미리 불러도 유휴 스레드가 생기지 않습니다.
 */
export function preloadDracoDecoder(decoderPath: string): void {
  getSharedDecoder(decoderPath).preload();
}

/*
 * drei가 자기 Draco 싱글턴으로 우리 것을 덮어쓰지 않게 끕니다.
 * (core/Gltf.js의 extensions()는 extendLoader를 먼저 부르고, useDraco가 참이면
 *  그 뒤에 자기 로더로 setDRACOLoader를 다시 호출합니다.)
 */
const LET_DREI_ATTACH_DRACO = false;
/** Meshopt는 drei 기본값(활성)을 그대로 씁니다. 디코더가 번들에 함께 들어 있습니다. */
const LET_DREI_ATTACH_MESHOPT = true;

function attachSharedDecoder(decoderPath: string) {
  return (loader: { setDRACOLoader: (decoder: DRACOLoader) => unknown }) => {
    loader.setDRACOLoader(getSharedDecoder(decoderPath));
  };
}

/** 차량 GLB를 로드합니다. Suspense 경계 안에서 호출해야 합니다. */
export function useCarGltf(asset: CarAsset): GLTF & ObjectMap {
  return useGLTF(
    asset.url,
    LET_DREI_ATTACH_DRACO,
    LET_DREI_ATTACH_MESHOPT,
    attachSharedDecoder(asset.dracoDecoderPath)
  );
}

/**
 * 차량 GLB를 미리 받아 둡니다. 렌더 밖(이벤트 핸들러·모듈 스코프)에서 부릅니다.
 *
 * 이미 받았거나 받는 중이면 drei의 캐시가 무시하므로 여러 번 불러도 안전합니다.
 */
export function preloadCarAsset(asset: CarAsset): void {
  useGLTF.preload(
    asset.url,
    LET_DREI_ATTACH_DRACO,
    LET_DREI_ATTACH_MESHOPT,
    attachSharedDecoder(asset.dracoDecoderPath)
  );
}

/**
 * 첫 화면에 뜨는 모델과 디코더만 미리 받습니다.
 *
 * 카탈로그를 다 preload하면 16MB를 한꺼번에 요청하게 되는데, 대부분은 기본 모델만
 * 보고 떠나므로 낭비입니다. 나머지는 카드에 포인터가 닿는 순간 받고
 * (ModelSelector), 그동안 ViewerLoadingOverlay가 진행률을 보여 줍니다.
 *
 * `public/index.html`에도 같은 두 대상에 `rel="preload"` 힌트가 있습니다. 이 모듈은
 * 번들이 평가된 뒤에야 실행되므로, 번들 다운로드·파싱과 겹치게 하려면 HTML 쪽 힌트가
 * 필요합니다. 둘이 어긋나지 않는지는 assetIntegrity.test.ts가 검사합니다.
 *
 * DEFAULT_CAR_MODEL_ID는 카탈로그 첫 항목의 id라서 해석이 실패할 수 없습니다.
 */
const defaultAsset = findCarModelById(DEFAULT_CAR_MODEL_ID)!.asset;
preloadDracoDecoder(defaultAsset.dracoDecoderPath);
preloadCarAsset(defaultAsset);
